import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  AssortmentMinimum,
  AssortmentReport,
  ProductQuality
} from './procurement-assortment.types';
import {
  AnalogueSource,
  AnalyticsAnalogues,
  AnalyticsDocuments,
  AnalyticsFilters,
  AnalyticsLookups,
  AnalyticsMinimumStockSuggestions,
  AnalyticsReport,
  AnalyticsSeries,
  AnalyticsWarehouseMinimumStock
} from './procurement-analytics.types';

@Injectable({ providedIn: 'root' })
export class ProcurementAnalyticsService {
  private readonly baseUrl = environment.BACKEND + '/procurement-analytics';

  constructor(private http: HttpClient) {}

  lookups() {
    return this.http.get<AnalyticsLookups>(this.baseUrl + '/lookups');
  }

  minimumStocks(productId: number) {
    return this.http.get<AnalyticsWarehouseMinimumStock[]>(
      `${this.baseUrl}/products/${productId}/minimum-stock`
    );
  }

  minimumStockSuggestions(
    productId: number,
    dateFrom: string,
    dateTo: string,
    coverageDays: number
  ) {
    return this.http.post<AnalyticsMinimumStockSuggestions>(
      `${this.baseUrl}/products/${productId}/minimum-stock/suggestions`,
      { dateFrom, dateTo, coverageDays }
    );
  }

  assortment(
    productId: number,
    payload: {
      dateFrom: string;
      dateTo: string;
      coverageDays: number;
      sources: AnalogueSource[];
      warehouseId?: number;
      page: number;
      pageSize: number;
      sortBy: string;
      sortDirection: 'asc' | 'desc';
    }
  ) {
    return this.http.post<AssortmentReport>(
      `${this.baseUrl}/products/${productId}/assortment`,
      payload
    );
  }

  saveAssortmentMinimums(
    productId: number,
    payload: {
      sources: AnalogueSource[];
      memberIds: number[];
      warehouseId: number;
      allocations: AssortmentMinimum[];
      expected: AssortmentMinimum[];
    }
  ) {
    return this.http.put<{ warehouseId: number; minimums: AssortmentMinimum[] }>(
      `${this.baseUrl}/products/${productId}/assortment/minimum-stock`,
      payload
    );
  }

  productQuality(productId: number) {
    return this.http.get<ProductQuality>(`${this.baseUrl}/products/${productId}/quality`);
  }

  saveProductQuality(
    productId: number,
    payload: { rating: number | null; comment: string; version: number }
  ) {
    return this.http.put<ProductQuality>(`${this.baseUrl}/products/${productId}/quality`, payload);
  }

  saveMinimumStock(productId: number, warehouseId: number, minimumStock: number, expectedMinimumStock: number) {
    return this.http.put<AnalyticsWarehouseMinimumStock>(
      `${this.baseUrl}/products/${productId}/minimum-stock`,
      { warehouseId, minimumStock, expectedMinimumStock }
    );
  }

  report(filters: AnalyticsFilters, includeWarehouseBreakdown = false, onlySoldProducts = false, includeMinimumStocks = false) {
    const { granularity, ...payload } = filters;
    return this.http.post<AnalyticsReport>(this.baseUrl + '/report', {
      ...payload,
      ...(includeMinimumStocks ? { includeMinimumStocks: true } : {}),
      ...(includeWarehouseBreakdown ? { includeWarehouseBreakdown: true } : {}),
      ...(onlySoldProducts ? { onlySoldProducts: true } : {})
    });
  }

  series(filters: AnalyticsFilters) {
    const days = (Date.parse(filters.dateTo) - Date.parse(filters.dateFrom)) / 86400000 + 1;
    return this.http.post<AnalyticsSeries>(this.baseUrl + '/series', {
      ...filters,
      granularity:
        filters.granularity !== 'auto'
          ? filters.granularity
          : days > 180
          ? 'month'
          : days > 31
          ? 'week'
          : 'day'
    });
  }

  documents(filters: AnalyticsFilters) {
    const { granularity, ...payload } = filters;
    return this.http.post<AnalyticsDocuments>(this.baseUrl + '/documents', payload);
  }

  analogues(productId: number, sources: AnalogueSource[], loadTecdoc = false) {
    return this.http.post<AnalyticsAnalogues>(this.baseUrl + '/analogues', {
      productId,
      sources,
      loadTecdoc
    });
  }
}
